'use client';
import { Badge, Group, Text, Progress, Image, Box, Flex } from '@mantine/core';
import styled, { css } from 'styled-components';
import numbro from 'numbro';
import { Carousel } from '@mantine/carousel';
import Button from '../Button';

const ESCAPIST_BUY_LINK = 'https://deposit.hideout.house/b/6oEcNBg5bgHU4ik288'; //'https://buy.hideout.house/b/9AQ9D1aJOcTCeSk9AF';
const NOMAD_BUY_LINK = 'https://deposit.hideout.house/b/6oEcNBg5bgHU4ik288'; // 'https://buy.hideout.house/b/fZecPdcRW9HqcKcfZ4';

const batchData: { escapist: any; nomad: any } = {
  escapist: {
    msrp: 79000,
    bulkPrice: 59000,
    batchSize: 10,
    displayName: 'Escapist',
    reservedCount: 13,
    learnMoreUrl: 'https://hideout.house/cabins/the-escapist',
    images: [
      'https://cdn.prod.website-files.com/65e0da8edae1c46e590f55e2/66918cc5cabde77ced442741_P1803285-p-2600.webp',
      'https://cdn.prod.website-files.com/65e0da8edae1c46e590f55e2/66918cd4bfe24407499f35af_P1814056-p-1080.webp',
      'https://cdn.prod.website-files.com/65e0da8edae1c46e590f55e2/66918d7461a0eb5661318274_P1803280-p-1080.webp',
      'https://cdn.prod.website-files.com/65e0da8edae1c46e590f55e2/66918de06c8f8d2b1c1c25ed_P1803320-p-1080.webp',
      'https://cdn.prod.website-files.com/65e0da8edae1c46e590f55e2/66918d78ae17b6298014aa56_P1814066-p-1080.webp',
      'https://cdn.prod.website-files.com/65e0da8edae1c46e590f55e2/66918d797607fa6807911e5d_P1803305-p-1080.webp'
    ],
    buyLink: ESCAPIST_BUY_LINK
  },
  nomad: {
    msrp: 99000,
    bulkPrice: 69000,
    batchSize: 10,
    reservedCount: 7,
    displayName: 'Nomad',
    learnMoreUrl: 'https://hideout.house/cabins/the-nomad',
    images: [
      'https://cdn.prod.website-files.com/65e0da8edae1c46e590f55e2/65e0da8edae1c46e590f5799_NOMAD_WINDOW_VIEW_INTERIOR.webp',
      'https://cdn.prod.website-files.com/65e0da8edae1c46e590f55e2/65e6a416b69a802485da868f_nomad-long.webp',
      'https://cdn.prod.website-files.com/65e0da8edae1c46e590f55e2/65e0da8edae1c46e590f579a_NOMAD_BUNK_VIEW_INTERIOR.webp',
      'https://cdn.prod.website-files.com/65e0da8edae1c46e590f55e2/65e0da8edae1c46e590f579b_NOMAD_BATHROOM.webp'
      // 'https://cdn.prod.website-files.com/65e0da8edae1c46e590f55e2/66918d78ae17b6298014aa56_P1814066-p-1080.webp',
      // 'https://cdn.prod.website-files.com/65e0da8edae1c46e590f55e2/66918d797607fa6807911e5d_P1803305-p-1080.webp'
    ],
    buyLink: NOMAD_BUY_LINK
  }
};

export default function GroupBuyPageWrapper({
  model = 'escapist',
  showImages = false
}: {
  model: 'escapist' | 'nomad';
  showImages: boolean;
}) {
  const { images, batchSize, reservedCount, msrp, bulkPrice, buyLink, displayName, learnMoreUrl } =
    batchData[model];

  const REMAINING = batchSize - reservedCount;
  const DISCOUNT_AMOUNT = msrp - bulkPrice;

  const MSRP_FORMATTED = numbro(msrp).formatCurrency({
    thousandSeparated: true,
    mantissa: 0
  });
  const BULK_PRICE_FORMATTED = numbro(bulkPrice).formatCurrency({
    thousandSeparated: true,
    mantissa: 0
  });
  const DISCOUNT_AMOUNT_FORMATTED = numbro(DISCOUNT_AMOUNT).formatCurrency({
    thousandSeparated: true,
    mantissa: 0
  });

  const MORE_INFO_URL =
    window.location.pathname.indexOf('/group-buy') === -1
      ? 'https://hideout.house/cabins/group-buy'
      : learnMoreUrl;

  const slides = images.map((url: string) => (
    <Carousel.Slide key={url}>
      <Image
        radius="sm"
        src={url}
        style={{ objectFit: 'cover', width: '100%', aspectRatio: '8/6' }}
      />
    </Carousel.Slide>
  ));

  return (
    <PageWrapper>
      <Container showImages={showImages}>
        {showImages && (
          <ImageWrapper>
            <Carousel loop withIndicators>
              {slides}
            </Carousel>
          </ImageWrapper>
        )}
        <InfoWrapper>
          <Header showImages={showImages}>
            <TitleWrapper>
              <h1 className="text-4xl font-bold">
                {showImages ? `The ${displayName}: Group Offer` : 'Special Group Offer'}
              </h1>
            </TitleWrapper>

            <PriceWrapper>
              <StrikeText className="text-4xl" color="dimmed">
                {MSRP_FORMATTED}
              </StrikeText>

              <PriceText className="text-4xl font-bold">{BULK_PRICE_FORMATTED}</PriceText>
            </PriceWrapper>
            <Badge size="lg" mt={10} variant="outline" color="green">
              {DISCOUNT_AMOUNT_FORMATTED} Group Discount
            </Badge>
          </Header>
          <div>
            <Description>
              Take advantage of our exclusive group buying discount. When we collectively secure
              orders for a batch of {batchSize} cabins, the price drops to {BULK_PRICE_FORMATTED}{' '}
              per cabin — a {DISCOUNT_AMOUNT_FORMATTED} savings from the original price!{' '}
            </Description>
            <Description>
              {' '}
              You can commit to purchasing one or more cabins. Other participants will cover the
              remaining units to reach the total batch size. Together, we achieve the discount for
              everyone!
            </Description>

            {!showImages && (
              <Description>
                <b>How It Works:</b>
                <ol>
                  <li>Place a Deposit: Secure your spot in the group buy with a $500 deposit.</li>
                  <li>
                    Discount Activation: Once a total of {batchSize} deposits are collected from all
                    participants, the price drops to {BULK_PRICE_FORMATTED} per cabin for everyone
                    in the group.
                  </li>
                  <li>
                    Purchase Your Cabin: After the batch closes, we'll reach out to set up a
                    purchase agreement. We offer financing options with as little as 20% down.
                  </li>
                </ol>
              </Description>
            )}
            <ProgressWrapper>
              <ProgressBarWrapper>
                <Progress
                  animated
                  size="xl"
                  color="green"
                  value={(reservedCount / batchSize) * 100}
                />
              </ProgressBarWrapper>
            </ProgressWrapper>
            <Flex justify="space-between">
              <Text>0 Deposits</Text>
              <Text>10 Deposits</Text>
            </Flex>
            <RemainingText size="lg" style={{ fontWeight: 500 }}>
              {reservedCount >= batchSize
                ? `Goal met! You can still join the active batch.`
                : `${REMAINING} more deposit${REMAINING > 1 ? 's' : ''} needed to unlock the discounted
              price!`}
            </RemainingText>
          </div>
          <ButtonGroup>
            <DepositButton externalLink={buyLink}>Place $500 Deposit</DepositButton>
            <InfoButton externalLink={MORE_INFO_URL}>More Info</InfoButton>
          </ButtonGroup>
        </InfoWrapper>
      </Container>
    </PageWrapper>
  );
}

const ButtonGroup = styled(Group)``;

const PageWrapper = styled.div`
  max-width: 1400px;
  margin: auto;
`;

const Container = styled.div<any>`
  max-width: 1400px;

  padding: 12px 24px;
  display: flex;
  flex-direction: column;
  gap: 18px;

  ${({ showImages }) =>
    !showImages
      ? css`
          max-width: 1080px;
          align-items: center !important;
          margin: 0 auto;
        `
      : ''}

  @media (min-width: 768px) {
    flex-direction: row;
    gap: 48px;
    padding: 20px 36px;
  }
`;

const ImageWrapper = styled.div`
  flex: 1;
`;

const InfoWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Header = styled.div<{ showImages: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  ${({ showImages }) =>
    !showImages
      ? css`
          align-items: center;
        `
      : ''}
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  @media (max-width: 768px) {
    text-align: center;
    h1 {
      font-size: 28px !important;
    }
  }
`;

const PriceWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Description = styled(Text)`
  color: '#ccc';
  margin-bottom: 20px !important;
  font-size: 18px !important;
  ol {
    padding-left: 20px;
    li {
      margin-top: 10px;
    }
  }

  @media (max-width: 768px) {
    font-size: 16px !important;
  }
`;

const NoteText = styled.p`
  margin-top: 10px;
`;

const ProgressWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ProgressBarWrapper = styled.div`
  flex: 1;
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const PriceText = styled<any>(Text)`
  font-weight: 500;
  font-size: 2.25rem !important; /* 36px */
  line-height: 2.5rem !important; /* 40px */
  @media (max-width: 768px) {
    font-size: 1.75rem !important;
    line-height: 1.7rem !important;
  }
`;

const StyledButton = styled<any>(Button)`
  align-self: center !important;
  max-width: auto;
  width: auto;
  @media (max-width: 768px) {
    width: 100%;
    max-width: none;
  }
`;

const DepositButton = styled(StyledButton)`
  flex: 1;
  max-width: 330px;
  @media (max-width: 768px) {
    width: 100%;
    max-width: none;
  }
`;

const InfoButton = styled(StyledButton)`
  background-color: #fff;
  color: #000;
  border: 1px solid #000;
`;

const StrikeText = styled<any>(PriceText)`
  position: relative;
  &:after {
    content: '';
    position: absolute;
    top: 50%;
    left: -5%;
    height: 4px;
    background: #000;
    opacity: 0.3;
    width: 110%;
    /* animation: strike 6s linear infinite; */
  }

  @keyframes strike {
    0% {
      width: 0;
    }
    25% {
      width: 110%;
    }
    50% {
      width: 0%;
    }
    100% {
      width: 0%;
    }
  }
`;

const RemainingText = styled<any>(Text)`
  margin-top: 16px !important;
  font-size: 18px !important;
  text-align: left !important;
  line-height: 19px !important;

  @media (max-width: 768px) {
    text-align: center !important;
  }
`;
